import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import cx from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types/swiper-options';

import { BREAKPOINTS_VALUES, useIsWidthUp } from '../../theme';
import { Background, Pathway as PathwayType, PathwayItem, PathwayListBlock } from '../../types';
import { splitIntoChunks } from '../../utils/helpers';
import { Pathway } from '../Pathway';
import { Select } from '../Select';
import { CategoryType, secondsToPathwaysFormat, splitPathwayItemsByCategories } from './helpers';
import useStyles from './styles';

const CHUNK_LENGTH = 2;

const swiperSettings: SwiperOptions = {
  slidesPerView: 4.5,
  spaceBetween: 16,
  pagination: {
    clickable: true,
  },
  navigation: true,
  mousewheel: {
    releaseOnEdges: true,
    forceToAxis: true,
    invert: true,
  },
  slidesOffsetAfter: 16,
  breakpoints: {
    [BREAKPOINTS_VALUES.xl]: {
      slidesPerView: 4.5,
    },
    [BREAKPOINTS_VALUES.lg]: {
      slidesPerView: 4.5,
    },
    [BREAKPOINTS_VALUES.md]: {
      slidesPerView: 3.2,
    },
    [BREAKPOINTS_VALUES.sm]: {
      slidesPerView: 2.2,
    },
    [BREAKPOINTS_VALUES.xs]: {
      slidesPerView: 1,
      spaceBetween: 36,
    },
  },
  observer: true,
};

interface PathwaysListProps {
  data?: PathwayListBlock;
}

const PathwaysListBlock: FC<PathwaysListProps> = ({ data }) => {
  const background = data?.background;
  const heading = data?.heading;
  const withCategories = data?.with_categories;

  const [hasMaxWidth, setHasMaxWidth] = useState(true);
  const [categoriesArray, setCategoriesArray] = useState<CategoryType[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<CategoryType | null>(null);
  const [formattedData, setFormattedData] = useState<Map<string | undefined, PathwayItem[]> | null>(
    null,
  );

  const classes = useStyles(background, hasMaxWidth)();
  const isMobile = !useIsWidthUp('md');
  const isSmallMobile = !useIsWidthUp('sm');
  const selectWrapRef = useRef<HTMLDivElement | null>(null);

  const pathways = useMemo(() => {
    return (data?.items?.filter((item) => item?.pathway) as PathwayItem[]) || [];
  }, [data]);
  const pathwaysArr = useMemo(() => {
    const arr = withCategories ? formattedData?.get(selectedCategory?.value) : pathways;
    return isSmallMobile ? splitIntoChunks(arr, CHUNK_LENGTH) : arr;
  }, [withCategories, formattedData, pathways, selectedCategory, isSmallMobile]);

  useEffect(() => {
    setTimeout(() => {
      setHasMaxWidth(false);
    }, 0);
  }, []);

  useEffect(() => {
    if (withCategories) {
      const { result, categoriesArray } = splitPathwayItemsByCategories(pathways);

      setCategoriesArray(categoriesArray);
      setFormattedData(result);
      if (categoriesArray.length > 0) {
        setSelectedCategory(categoriesArray[0]);
      }
    }
  }, [pathways, withCategories]);

  return (
    <Container
      className={cx(
        classes.container,
        background === 'Dark' ? 'dark-block' : 'light-block',
        'pathways-container',
        'pathways-block',
      )}
      maxWidth={false}
    >
      <Box className={classes.headingWrapper}>
        <Typography variant="h2" component="h2" align="center" className={classes.heading}>
          {heading}
        </Typography>

        {withCategories &&
          Boolean(categoriesArray?.length) &&
          (isMobile ? (
            <div className={classes.selectorWrapper} ref={selectWrapRef}>
              <Select
                value={selectedCategory}
                items={categoriesArray}
                onChange={(val) => setSelectedCategory(val)}
                anchorRef={selectWrapRef}
                selectBtnClass={classes.selectBtnClass}
                selectHeaderClass={classes.selectHeaderClass}
              />
            </div>
          ) : (
            <div className={classes.categoriesWrapper}>
              {categoriesArray.map((category) => (
                <div
                  data-testid="category"
                  key={category.value}
                  className={cx(
                    classes.categoryItem,
                    category.value === selectedCategory?.value && classes.activeCategory,
                  )}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category.display}
                </div>
              ))}
            </div>
          ))}
      </Box>

      {pathwaysArr && (
        <Swiper {...swiperSettings}>
          {!isSmallMobile
            ? pathwaysArr?.map((item, i) => {
                const { image_url, name, videos, duration, count_videos, slug } =
                  item?.pathway as PathwayType;
                const fullDuration = duration
                  ? duration
                  : videos?.map((v) => v?.duration || 0)?.reduce((acc, d) => acc + d, 0);
                const countVideos = count_videos ? count_videos : videos?.length;
                return (
                  <SwiperSlide key={i}>
                    <div className={classes.itemWrapper}>
                      <Pathway
                        videoWidth={0}
                        duration={secondsToPathwaysFormat(fullDuration)}
                        countVideos={countVideos}
                        imageUrl={image_url}
                        name={name}
                        wrapperClass={classes.pathway}
                        withHover={false}
                        background={isSmallMobile ? Background.Dark : background}
                        slug={slug}
                      />
                    </div>
                  </SwiperSlide>
                );
              })
            : pathwaysArr?.map((chunk, i) => (
                <SwiperSlide key={i}>
                  <div className={classes.chunkWrapper}>
                    {chunk.map((item, i) => {
                      const { image_url, name, videos, duration, count_videos, slug } =
                        item?.pathway as PathwayType;
                      const fullDuration = duration
                        ? duration
                        : videos?.map((v) => v?.duration || 0)?.reduce((acc, d) => acc + d, 0);
                      const countVideos = count_videos ? count_videos : videos?.length;
                      return (
                        <div className={classes.itemWrapper} key={i}>
                          <Pathway
                            videoWidth={0}
                            duration={secondsToPathwaysFormat(fullDuration)}
                            countVideos={countVideos}
                            imageUrl={image_url}
                            name={name}
                            wrapperClass={classes.pathway}
                            withHover={false}
                            background={isSmallMobile ? Background.Dark : background}
                            slug={slug}
                          />
                        </div>
                      );
                    })}
                  </div>
                </SwiperSlide>
              ))}
        </Swiper>
      )}
    </Container>
  );
};

export default PathwaysListBlock;
